import { LeadSourcesPage } from "./LeadSourcesPage";
import { ReferralsPage } from "./ReferralsPage";
import { MailchimpPage } from "./MailchimpPage";
import { useState, type ReactNode } from "react";
import { TodoDock } from "../../components/TodoDock";
import { Link, Route, Switch, useLocation } from "wouter";
import { JobDetail } from "./JobDetail";
import { PipelinePage } from "./PipelinePage";
import { ProjectsPage } from "./ProjectsPage";
import { WorkOrdersPage } from "./WorkOrdersPage";
import { IntakePage } from "./IntakePage";
import { InventoryPage } from "./InventoryPage";
import { TemplatesPage } from "./TemplatesPage";
import { ExpensesPage } from "./ExpensesPage";
import { PurchasingPage } from "./PurchasingPage";
import { AgreementsPage } from "./AgreementsPage";
import {
  BarChart3,
  BookOpen,
  Boxes,
  Briefcase,
  ChevronDown,
  FileSignature,
  FileUp,
  FolderOpen,
  MailSearch,
  FolderKanban,
  KanbanSquare,
  CalendarClock,
  ClipboardCheck,
  Banknote,
  Clock3,
  CreditCard,
  FileText,
  Import,
  Inbox,
  LifeBuoy,
  LogOut,
  MapPin,
  LayoutDashboard,
  MessageSquareText,
  Receipt,
  Repeat,
  ShieldCheck,
  ShoppingCart,
  UserCircle,
  Users,
  Wallet,
  Mail,
  Gift,
  Phone,
} from "lucide-react";
import { ClevisMark, cn } from "@clevis/shared-ui";
import { useAuth } from "../../hooks/useAuth";
import { useAccess } from "../../hooks/useAccess";
import { SidebarNav, type NavEntry } from "../../components/SidebarNav";
import { AccessPage } from "./AccessPage";
import { TwoStepGate } from "../../components/TwoStepGate";
import { ProfilePage } from "./ProfilePage";
import { TextingPage } from "./TextingPage";
import { DocumentsPage } from "./DocumentsPage";
import { PaymentsPage } from "./PaymentsPage";
import { ImpersonationBanner } from "../../components/ImpersonationBanner";
import { Dashboard } from "./Dashboard";
import { CustomersPage } from "./CustomersPage";
import { JobsPage } from "./JobsPage";
import { SchedulePage } from "./SchedulePage";
import { TimeClock } from "../workforce/TimeClock";
import { ShiftBoard } from "../workforce/ShiftBoard";
import { JobsNearMe } from "../workforce/JobsNearMe";
import { TimesheetsAdmin } from "../workforce/TimesheetsAdmin";
import { TeamPage } from "./TeamPage";
import { SupportPage } from "../support/SupportPage";
import { BillingPage } from "./BillingPage";
import { MigrationPage } from "./MigrationPage";
import { MigrationHub } from "./MigrationHub";
import { BuilderCleanupPage } from "./BuilderCleanupPage";
import { CustomerDetail } from "./CustomerDetail";
import { QuickBooksPage } from "./QuickBooksPage";
import { SalesPage } from "../sales/SalesPage";
import { RequestsPage } from "./RequestsPage";
import { PricebookPage } from "../sales/PricebookPage";
import { ReportsPage } from "./ReportsPage";
import { useEntitlements } from "../../hooks/useEntitlements";
import { useScrollTopOnNavigate } from "../../hooks/useScrollTopOnNavigate";
import { MORE_GROUP, placeItems, useMenuLayout } from "../../hooks/useMenuLayout";
import { PlanLocked } from "../../components/PlanLocked";
import { useIndustry } from "../../hooks/useIndustry";
import { MessageBanner } from "../../components/MessageBanner";
import { IncomingCallBanner } from "../../components/IncomingCallBanner";
import { RemoteSupportProvider } from "../../components/RemoteSupportProvider";

// A customer page with its own URL (projects link back to their builder).
function CustomerDetailRoute({ customerId }: { customerId: string }) {
  const [, navigate] = useLocation();
  return <CustomerDetail customerId={customerId} onBack={() => navigate("/customers")} />;
}

// Deep link target for dashboard/pipeline rows: a job page with its own URL.
function JobDetailRoute({ jobId }: { jobId: string }) {
  const [, navigate] = useLocation();
  return <JobDetail jobId={jobId} onBack={() => navigate("/jobs")} />;
}

// Nav lives in collapsible groups — nobody (child or dispatcher) should face
// 30 flat items. The standard four below, unless the owner set the company's
// own (Roles & access → Company menu). Mobile keeps its own short tab bar.
type NavItem = NavEntry & { mobile: boolean; feature?: string; integrationsAny?: readonly string[] };
// Every item is a section (shared-types access.ts): role defaults + the
// owner's access settings decide who sees it, here and on the server.
const navItems: NavItem[] = [
  { id: "dashboard", href: "/", label: "Dashboard", icon: LayoutDashboard, mobile: true, group: "Work" },
  { id: "schedule", href: "/schedule", label: "Schedule", icon: CalendarClock, mobile: true, group: "Work" },
  { id: "pipeline", href: "/pipeline", label: "Pipeline", icon: KanbanSquare, mobile: false, group: "Work" },
  { id: "jobs", href: "/jobs", label: "Jobs", icon: Briefcase, mobile: true, group: "Work" },
  { id: "requests", href: "/requests", label: "Requests", icon: Inbox, mobile: false, group: "Work", feature: "requests" },
  { id: "projects", href: "/projects", label: "Projects", icon: FolderKanban, mobile: false, group: "Work", feature: "projects" },
  { id: "workorders", href: "/workorders", label: "Work orders", icon: FileUp, mobile: false, group: "Work", feature: "emailIntake" },
  // Admin/owner always; managers only when the owner shared the queue.
  { id: "intake", href: "/intake", label: "Email intake", icon: MailSearch, mobile: false, group: "Work", feature: "emailIntake" },
  { id: "customers", href: "/customers", label: "Customers", icon: Users, mobile: false, group: "Work" },
  { id: "referrals", href: "/referrals", label: "Referrals", icon: Gift, mobile: false, group: "Work", feature: "referrals" },
  { id: "estimates", href: "/estimates", label: "Estimates", icon: FileText, mobile: false, group: "Money" },
  { id: "invoices", href: "/invoices", label: "Invoices", icon: Receipt, mobile: false, group: "Money" },
  { id: "expenses", href: "/expenses", label: "Expenses", icon: Wallet, mobile: false, group: "Money", feature: "expenses" },
  { id: "pricebook", href: "/pricebook", label: "Pricebook", icon: BookOpen, mobile: false, group: "Money" },
  { id: "inventory", href: "/inventory", label: "Inventory", icon: Boxes, mobile: false, group: "Money", feature: "inventory" },
  { id: "reports", href: "/reports", label: "Reports", icon: BarChart3, mobile: false, group: "Money", feature: "reporting" },
  { id: "purchasing", href: "/purchasing", label: "Purchase orders", icon: ShoppingCart, mobile: false, group: "Money", feature: "purchasing" },
  { id: "agreements", href: "/agreements", label: "Maintenance agreements", icon: Repeat, mobile: false, group: "Money", feature: "serviceAgreements" },
  { id: "timeclock", href: "/timeclock", label: "Time Clock", icon: Clock3, mobile: true, group: "Team", feature: "workforce" },
  { id: "shifts", href: "/shifts", label: "Shifts", icon: CalendarClock, mobile: false, group: "Team", feature: "shiftMarketplace" },
  { id: "nearby", href: "/nearby", label: "Near Me", icon: MapPin, mobile: false, group: "Team", feature: "shiftMarketplace" },
  { id: "timesheets", href: "/timesheets", label: "Timesheets", icon: ClipboardCheck, mobile: false, group: "Team", feature: "workforce" },
  { id: "team", href: "/team", label: "Team", icon: Users, mobile: false, group: "Team" },
  { id: "profile", href: "/profile", label: "My profile", icon: UserCircle, mobile: false, group: "Team" },
  { id: "templates", href: "/templates", label: "Templates", icon: FileSignature, mobile: false, group: "Setup", feature: "templates" },
  { id: "support", href: "/support", label: "Support", icon: LifeBuoy, mobile: true, group: "Setup" },
  { id: "texting", href: "/texting", label: "Texting", icon: MessageSquareText, mobile: false, group: "Setup" },
  { id: "leads", href: "/leads", label: "Clevis Ring", icon: Phone, mobile: false, group: "Setup" },
  { id: "mailchimp", href: "/mailchimp", label: "Mailchimp", icon: Mail, mobile: false, group: "Setup", feature: "emailMarketing" },
  { id: "documents", href: "/documents", label: "Company documents", icon: FolderOpen, mobile: false, group: "Setup" },
  { id: "payments", href: "/payments", label: "Card payments", icon: Banknote, mobile: false, group: "Setup", integrationsAny: ["stripe-payments"] },
  { id: "billing", href: "/billing", label: "Billing", icon: CreditCard, mobile: false, group: "Setup" },
  { id: "migration", href: "/migration", label: "Migration", icon: Import, mobile: false, group: "Setup" },
  {
    id: "quickbooks",
    href: "/quickbooks",
    label: "QuickBooks",
    icon: BookOpen,
    mobile: false,
    group: "Setup",
    integrationsAny: ["quickbooks-online", "quickbooks-desktop"],
    feature: "accountingSync",
  },
  { id: "access", href: "/access", label: "Roles & access", icon: ShieldCheck, mobile: false, group: "Setup", feature: "customAccess" },
];


function openGroupsPref(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem("clevis.navGroups") ?? "") as Record<string, boolean>;
  } catch {
    return {}; // the first two groups start open
  }
}

export function CustomerShell() {
  const { user, role, tenantId, signOut } = useAuth();
  const [location] = useLocation();
  const { hasFeature, hasIntegration } = useEntitlements();
  useScrollTopOnNavigate();
  const { vocab, intakeShared } = useIndustry();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(openGroupsPref);

  const menu = useMenuLayout(tenantId);
  const placed = placeItems(menu.groups);
  function toggleGroup(group: string, open: boolean) {
    setOpenGroups((prev) => {
      const next = { ...prev, [group]: open };
      try {
        localStorage.setItem("clevis.navGroups", JSON.stringify(next));
      } catch {
        /* private mode */
      }
      return next;
    });
  }
  const isTech = role === "tech";
  const can = useAccess();
  // Who sees what: the section rules (role defaults + owner's settings), then plan features.
  const featureOf = new Map(navItems.filter((i) => i.feature).map((i) => [i.id, i.feature!]));
  const gate = (id: string, el: ReactNode) => {
    if (!can(id)) return <NoAccess />;
    const f = featureOf.get(id);
    return f && !hasFeature(f) ? <PlanLocked feature={f} page /> : el;
  };
  const nav = navItems
    .filter(
      (i) =>
        can(i.id) &&
        (!i.feature || hasFeature(i.feature)) &&
        (!i.integrationsAny || i.integrationsAny.some((slug) => hasIntegration(slug))),
    )
    // Managers see Email intake only once the owner shares the queue.
    .filter((i) => i.href !== "/intake" || role === "admin" || role === "owner" || intakeShared)
    // Industry vocabulary: a landscape company sees "Work tickets", not "Jobs".
    .map((i) => (i.href === "/jobs" ? { ...i, label: isTech ? `My ${vocab.jobs.toLowerCase()}` : vocab.jobs } : i))
    // The office keeps Support in the sidebar only; a tech's phone tab bar has room for it.
    .map((i) => (i.href === "/support" && !isTech ? { ...i, mobile: false } : i))
    // The company's groups; an item it didn't place goes under More.
    .map((i) => ({ ...i, group: placed.groupOf.get(i.id) ?? MORE_GROUP.id }));
  // Inside a group, the company's order (each person can still reorder their own).
  if (menu.groups) {
    const pos = new Map(menu.groups.flatMap((g) => g.items).map((id, n) => [id, n] as const));
    nav.sort((a, b) => (pos.get(a.id) ?? 999) - (pos.get(b.id) ?? 999));
  }

  return (
    <RemoteSupportProvider>
    <div className="flex min-h-full flex-col">
      <ImpersonationBanner />
      <MessageBanner context="app" tenantId={tenantId} />
      {/* The caller pop: office roles, once the company has a tracking number to ring. */}
      {tenantId && !isTech && hasFeature("leadNumbers") ? <IncomingCallBanner tenantId={tenantId} /> : null}
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
      {/* Desktop sidebar */}
      {/* Pinned with its own scroll: changing pages sends only the main column to the top. */}
      <aside className="hidden w-56 shrink-0 flex-col overflow-y-auto bg-ink text-white md:sticky md:top-0 md:flex md:h-screen md:self-start">
        <div className="flex items-center gap-2 px-4 py-4">
          <ClevisMark size={28} body="#F4F1EB" />
          <div className="text-sm font-bold">Clevis</div>
        </div>
        <SidebarNav items={nav} groups={placed.groups} can={can} openGroups={openGroups} onToggleGroup={toggleGroup} />
        <div className="border-t border-white/10 p-3">
          <div className="truncate text-xs text-steel-light">
            {user?.email} · {role}
          </div>
          <button
            onClick={() => void signOut()}
            className="mt-2 flex items-center gap-2 text-xs text-steel-light hover:text-white"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 p-4 pb-20 md:p-6 md:pb-6">
        <TwoStepGate>
        {isTech ? (
          // Field Tech: their jobs are home; any office URL lands back there.
          <Switch>
            <Route path="/jobs/:id">{(params) => <JobDetailRoute jobId={params.id ?? ""} />}</Route>
            {/* Only when the owner gave Field techs the board (Roles & access). */}
            <Route path="/schedule">{gate("schedule", <SchedulePage />)}</Route>
            <Route path="/timeclock">{gate("timeclock", <TimeClock />)}</Route>
            <Route path="/shifts">{gate("shifts", <ShiftBoard />)}</Route>
            <Route path="/nearby">{gate("nearby", <JobsNearMe />)}</Route>
            <Route path="/support">{gate("support", <SupportPage />)}</Route>
            <Route path="/profile">{gate("profile", <ProfilePage />)}</Route>
            <Route>{gate("jobs", <JobsPage />)}</Route>
          </Switch>
        ) : (
        <Switch>
          <Route path="/schedule">{gate("schedule", <SchedulePage />)}</Route>
          <Route path="/pipeline">{gate("pipeline", <PipelinePage />)}</Route>
          <Route path="/projects/:id">{gate("projects", <ProjectsPage />)}</Route>
          <Route path="/projects">{gate("projects", <ProjectsPage />)}</Route>
          <Route path="/workorders">{gate("workorders", <WorkOrdersPage />)}</Route>
          <Route path="/intake">{gate("intake", <IntakePage />)}</Route>
          <Route path="/inventory">{gate("inventory", <InventoryPage />)}</Route>
          <Route path="/purchasing">{gate("purchasing", <PurchasingPage />)}</Route>
          <Route path="/agreements">{gate("agreements", <AgreementsPage />)}</Route>
          <Route path="/jobs/:id">{(params) => gate("jobs", <JobDetailRoute jobId={params.id ?? ""} />)}</Route>
          <Route path="/jobs">{gate("jobs", <JobsPage />)}</Route>
          <Route path="/customers/:id">{(params) => gate("customers", <CustomerDetailRoute customerId={params.id ?? ""} />)}</Route>
          <Route path="/customers">{gate("customers", <CustomersPage />)}</Route>
          <Route path="/requests">{gate("requests", <RequestsPage />)}</Route>
          <Route path="/estimates">{gate("estimates", <SalesPage kind="estimate" />)}</Route>
          <Route path="/invoices">{gate("invoices", <SalesPage kind="invoice" />)}</Route>
          <Route path="/expenses/:id">{gate("expenses", <ExpensesPage />)}</Route>
          <Route path="/expenses">{gate("expenses", <ExpensesPage />)}</Route>
          <Route path="/pricebook">{gate("pricebook", <PricebookPage />)}</Route>
          <Route path="/timeclock">{gate("timeclock", <TimeClock />)}</Route>
          <Route path="/shifts">{gate("shifts", <ShiftBoard />)}</Route>
          <Route path="/nearby">{gate("nearby", <JobsNearMe />)}</Route>
          <Route path="/timesheets">{gate("timesheets", <TimesheetsAdmin />)}</Route>
          <Route path="/reports">{gate("reports", <ReportsPage />)}</Route>
          <Route path="/templates">{gate("templates", <TemplatesPage />)}</Route>
          <Route path="/team">{gate("team", <TeamPage />)}</Route>
          <Route path="/support">{gate("support", <SupportPage />)}</Route>
          <Route path="/billing">{gate("billing", <BillingPage />)}</Route>
          <Route path="/migration/builders">{gate("migration", <BuilderCleanupPage />)}</Route>
          <Route path="/migration/fieldpulse">{gate("migration", <MigrationPage />)}</Route>
          <Route path="/migration">{gate("migration", <MigrationHub />)}</Route>
          <Route path="/quickbooks">{gate("quickbooks", <QuickBooksPage />)}</Route>
          <Route path="/access">{gate("access", <AccessPage />)}</Route>
          <Route path="/profile">{gate("profile", <ProfilePage />)}</Route>
          <Route path="/texting">{gate("texting", <TextingPage />)}</Route>
          <Route path="/leads">{gate("leads", <LeadSourcesPage />)}</Route>
          <Route path="/referrals">{gate("referrals", <ReferralsPage />)}</Route>
          <Route path="/mailchimp">{gate("mailchimp", <MailchimpPage />)}</Route>
          <Route path="/documents">{gate("documents", <DocumentsPage />)}</Route>
          <Route path="/payments">{gate("payments", <PaymentsPage />)}</Route>
          <Route>{gate("dashboard", <Dashboard />)}</Route>
        </Switch>
        )}
        </TwoStepGate>
      </main>

      {tenantId && user && !isTech ? <TodoDock tenantId={tenantId} uid={user.uid} /> : null}

      {/* Mobile bottom tab bar — the app shell for phones / Capacitor */}
      <nav className="fixed inset-x-0 bottom-0 z-10 flex border-t border-steel/20 bg-white md:hidden">
        {nav.filter((i) => i.mobile).map((item) => (
          <Link
            key={item.href}
            href={item.href}
            className={cn(
              "flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] text-steel",
              location === item.href && "text-brandorange",
            )}
          >
            <item.icon size={18} />
            {item.label}
          </Link>
        ))}
      </nav>
      </div>
    </div>
    </RemoteSupportProvider>
  );
}

function NoAccess() {
  return (
    <div className="mx-auto max-w-md py-16 text-center">
      <h1 className="text-lg font-bold text-ink">No access</h1>
      <p className="mt-1 text-sm text-steel">
        Your company's owner hasn't given you this page. Ask them to turn it on under Roles &amp; access.
      </p>
    </div>
  );
}
